import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  MicrophoneIcon,
  VideoCameraIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { useCandidateT } from '../../i18n/candidate';
import { isProdHost } from '../../lib/isProdHost';

/**
 * InterviewReadinessModal (org feature `ai_interview_readiness_check`)
 *
 * Opens from Start Interview on the join page, in place of DeviceCheckModal.
 * 1. Voice warm-up: the candidate reads a sentence aloud and passes only once they have
 *    spoken loudly enough for long enough. Measured with the same mic settings as the
 *    interview (no auto gain), so a pass here means the interview hears them too. The point
 *    is that the candidate is already speaking up when the first question comes.
 * 2. Camera (video interviews only): 3 photos about 4 seconds apart, sent with the device
 *    type to POST /api/ai-interviews/session/:sessionId/readiness-check. A vision model checks
 *    seating, framing, lighting and clothing and returns one line for the candidate. Any
 *    server or network failure lets the candidate through.
 *
 * Props: isOpen, onClose, onProceed, interviewType ('voice' | 'video'), sessionId, sessionToken
 */

// Voice warm-up. One verdict per reading of the sentence, not per moment of sound: the
// "active speech level" (ITU-T P.56 idea) is the energy average over the frames where the
// candidate is speaking, pauses excluded. It is steady from one try to the next, unlike
// per-frame levels, which swing 10-20 dB between words. Tune here; dev hosts show the numbers.
// Two gates, both required. The level alone cannot catch a soft voice on a sensitive laptop
// mic (Windows mic boost lifts a soft voice past -28), so the voice must also stand far above
// the room's own hiss. Mic gain lifts voice and hiss together, so that gap tracks how hard
// the candidate speaks: soft speech in a quiet room sits ~20 dB above it, a raised voice 30+.
// Active speech level (dBFS) the sentence must reach.
const TARGET_LEVEL_DB = -24;
// Gap between the voice and the room noise.
const MIN_SNR_DB = 28;
// Room noise above this is "noisy" (move somewhere quieter), below it a small gap means "soft".
const NOISY_ROOM_DB = -50;
// A frame is speech when it is this far above the room noise (and above SPEECH_FLOOR_DB).
const SPEECH_ABOVE_NOISE_DB = 10;
const SPEECH_FLOOR_DB = -55;
// Room noise estimate: follows the quietest level, creeps up slowly, never below this.
const NOISE_RISE_DB_PER_S = 1;
const NOISE_MIN_DB = -75;
// Verdict after at least MIN_SPEECH_MS of speech followed by an END_PAUSE_MS pause (the
// sentence is done), or at MAX_SPEECH_MS of speech. No usable speech by LISTEN_TIMEOUT_MS: retry.
const MIN_SPEECH_MS = 2000;
const END_PAUSE_MS = 700;
const MAX_SPEECH_MS = 6000;
const LISTEN_TIMEOUT_MS = 15000;
// Live meter smoothing only; the verdict uses raw frames.
const METER_SMOOTHING_MS = 120;
// Meter scale: -60 dBFS = 0, 0 dBFS = 100.
const dbToMeter = (db) => Math.max(0, Math.min(1, (db + 60) / 60));
const METER_TARGET = dbToMeter(TARGET_LEVEL_DB);

// Why a reading did not pass, with the line shown to the candidate.
const VOICE_RETRY_MESSAGES = {
  soft: 'Your voice was too soft. Please read the sentence again, a little louder.',
  noisy: 'There is a lot of noise around you. Move somewhere quieter, or speak louder, and try again.',
  unheard: "We couldn't hear you. Check that the right microphone is selected and read the sentence again.",
};

const powerToDb = (power) => 10 * Math.log10(power || 1e-10);

// Photo times after "Check me", in ms: a moment to settle, then 3 photos over ~10 seconds.
const PHOTO_TIMES_MS = [2000, 6000, 10000];
const PHOTO_MAX_SIDE = 640;
// After this many failed camera checks the candidate may continue anyway.
const ATTEMPTS_BEFORE_SKIP = 3;

// Fallback tips when the server sends no message.
const ISSUE_TIPS = {
  NO_PERSON: "We can't see you. Please sit in front of the camera.",
  MULTIPLE_PEOPLE: 'Only you should be in the camera view.',
  NOT_SEATED: 'Please sit down in one place and stay there for the interview.',
  FACE_NOT_VISIBLE: 'Keep your whole face visible and look at the screen.',
  TOO_FAR: 'Move closer to the camera.',
  TOO_CLOSE: 'Move a little back from the camera.',
  OFF_CENTER: 'Move so your face is in the middle of the frame.',
  POOR_LIGHTING: 'Your face is too dark. Sit facing a light or a window.',
  NOT_DRESSED: 'Please wear a shirt or T-shirt. Your interview is recorded on video.',
};

const MIC_CONSTRAINTS = { echoCancellation: true, noiseSuppression: false, autoGainControl: false };

const stopStream = (ref) => {
  if (ref.current) {
    ref.current.getTracks().forEach((track) => track.stop());
    ref.current = null;
  }
};

/** 'phone' | 'tablet' | 'desktop', from the user agent and touch support. */
export const detectDeviceType = () => {
  const ua = navigator.userAgent || '';
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iPad|Tablet/i.test(ua) || iPadOS || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
  if (/iPhone|iPod|Android|Mobile|IEMobile|Opera Mini/i.test(ua)) return 'phone';
  return 'desktop';
};

const InterviewReadinessModal = ({
  isOpen,
  onClose,
  onProceed,
  interviewType = 'voice',
  sessionId,
  sessionToken,
}) => {
  const isVideo = interviewType === 'video';
  const t = useCandidateT();

  // 'voice' | 'camera' | 'done'
  const [step, setStep] = useState('voice');

  // Voice: 'idle' | 'listening' | 'passed' | 'retry' | 'error'
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [level, setLevel] = useState(0);
  const [speechProgress, setSpeechProgress] = useState(0);
  const [speakLouder, setSpeakLouder] = useState(false);
  // Last verdict: { reason: 'soft' | 'noisy' | 'unheard' | null, voiceDb, noiseDb }
  const [voiceResult, setVoiceResult] = useState(null);

  // Camera: 'starting' | 'live' | 'capturing' | 'checking' | 'passed' | 'failed' | 'error'
  const [cameraStatus, setCameraStatus] = useState('starting');
  const [photosTaken, setPhotosTaken] = useState(0);
  const [portrait, setPortrait] = useState(false);
  const [result, setResult] = useState({ issues: [], message: '' });
  const [failedAttempts, setFailedAttempts] = useState(0);

  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const frameRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const videoRef = useRef(null);
  const timersRef = useRef([]);
  // Bumped on every new camera check and on close, so a late photo or response is ignored.
  const runRef = useRef(0);

  const stopMic = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    stopStream(micStreamRef);
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const cleanupAll = useCallback(() => {
    runRef.current += 1;
    clearTimers();
    stopMic();
    stopStream(cameraStreamRef);
  }, [clearTimers, stopMic]);

  useEffect(() => {
    if (isOpen) {
      setStep('voice');
      setVoiceStatus('idle');
      setLevel(0);
      setSpeechProgress(0);
      setSpeakLouder(false);
      setVoiceResult(null);
      setCameraStatus('starting');
      setPhotosTaken(0);
      setResult({ issues: [], message: '' });
      setFailedAttempts(0);
    } else {
      cleanupAll();
    }
  }, [isOpen, cleanupAll]);

  useEffect(() => () => cleanupAll(), [cleanupAll]);

  // --- Voice ---
  const startVoiceCheck = useCallback(async () => {
    stopMic();
    setVoiceStatus('listening');
    setLevel(0);
    setSpeechProgress(0);
    setSpeakLouder(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS });
      micStreamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') await audioContext.resume().catch(() => {});
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      audioContext.createMediaStreamSource(stream).connect(analyser);

      const samples = new Float32Array(analyser.fftSize);
      const startedAt = performance.now();
      let last = startedAt;
      let noiseDb = null;
      let meterDb = -100;
      // Speech so far: total time and time-weighted power, for the active speech level.
      let speechMs = 0;
      let speechEnergy = 0;
      let pauseMs = 0;

      const finish = (reason, voiceDb) => {
        stopMic();
        setLevel(0);
        const verdict = { reason, voiceDb: Math.round(voiceDb), noiseDb: Math.round(noiseDb ?? -100) };
        setVoiceResult(verdict);
        setVoiceStatus(reason ? 'retry' : 'passed');
        if (!isProdHost()) console.info('[READINESS_VOICE]', { ...verdict, speechMs: Math.round(speechMs) });
      };

      const tick = (now) => {
        if (!audioContextRef.current) return;
        const dt = Math.min(now - last, 100);
        last = now;
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
        const power = sum / samples.length;
        const db = powerToDb(power);

        noiseDb =
          noiseDb === null || db < noiseDb
            ? Math.max(db, NOISE_MIN_DB)
            : noiseDb + (NOISE_RISE_DB_PER_S * dt) / 1000;

        if (db >= Math.max(SPEECH_FLOOR_DB, noiseDb + SPEECH_ABOVE_NOISE_DB)) {
          speechMs += dt;
          speechEnergy += power * dt;
          pauseMs = 0;
        } else if (speechMs > 0) {
          pauseMs += dt;
        }
        const voiceDb = speechMs > 0 ? powerToDb(speechEnergy / speechMs) : -100;

        meterDb += (db - meterDb) * Math.min(1, dt / METER_SMOOTHING_MS);
        setLevel(dbToMeter(meterDb));
        setSpeechProgress(Math.min(1, speechMs / MIN_SPEECH_MS));
        setSpeakLouder(
          speechMs > MIN_SPEECH_MS / 2 &&
            (voiceDb < TARGET_LEVEL_DB || voiceDb - noiseDb < MIN_SNR_DB)
        );

        if (speechMs >= MIN_SPEECH_MS && (pauseMs >= END_PAUSE_MS || speechMs >= MAX_SPEECH_MS)) {
          if (voiceDb < TARGET_LEVEL_DB) finish('soft', voiceDb);
          else if (voiceDb - noiseDb < MIN_SNR_DB) finish(noiseDb > NOISY_ROOM_DB ? 'noisy' : 'soft', voiceDb);
          else finish(null, voiceDb);
          return;
        }
        if (now - startedAt >= LISTEN_TIMEOUT_MS) {
          finish('unheard', voiceDb);
          return;
        }
        frameRef.current = requestAnimationFrame(tick);
      };
      frameRef.current = requestAnimationFrame(tick);
    } catch (err) {
      console.error('Readiness voice check error:', err);
      stopMic();
      setVoiceStatus('error');
    }
  }, [stopMic]);

  // --- Camera ---
  const startCamera = useCallback(async () => {
    stopStream(cameraStreamRef);
    setCameraStatus('starting');
    try {
      // Desktop webcams default to 640x480; ask for 720p. Phones keep their natural portrait size.
      const video =
        detectDeviceType() === 'desktop'
          ? { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }
          : { facingMode: 'user' };
      const stream = await navigator.mediaDevices.getUserMedia({ video });
      cameraStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraStatus('live');
    } catch (err) {
      console.error('Readiness camera error:', err);
      setCameraStatus('error');
    }
  }, []);

  // The <video> can mount after the stream is ready; attach it then.
  useEffect(() => {
    if (step === 'camera' && videoRef.current && cameraStreamRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  });

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return null;
    const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  const sendPhotos = useCallback(
    async (runId, images) => {
      setCameraStatus('checking');
      const video = videoRef.current;
      const device = {
        type: detectDeviceType(),
        orientation: video && video.videoHeight > video.videoWidth ? 'portrait' : 'landscape',
      };
      try {
        const res = await axios.post(
          `/api/ai-interviews/session/${sessionId}/readiness-check`,
          { images, device },
          { headers: { 'x-session-token': sessionToken }, timeout: 45000 }
        );
        if (runRef.current !== runId) return;
        const issues = Array.isArray(res.data?.issues) ? res.data.issues : [];
        setResult({ issues, message: typeof res.data?.message === 'string' ? res.data.message : '' });
        if (res.data?.pass) {
          setCameraStatus('passed');
        } else {
          setFailedAttempts((n) => n + 1);
          setCameraStatus('failed');
        }
      } catch (err) {
        // Our failure, not the candidate's: never block them on it.
        console.error('Readiness camera check error:', err);
        if (runRef.current !== runId) return;
        setResult({ issues: [], message: '' });
        setCameraStatus('passed');
      }
    },
    [sessionId, sessionToken]
  );

  const startCameraCheck = useCallback(() => {
    clearTimers();
    runRef.current += 1;
    const runId = runRef.current;
    const images = [];
    setPhotosTaken(0);
    setResult({ issues: [], message: '' });
    setCameraStatus('capturing');

    PHOTO_TIMES_MS.forEach((delay, i) => {
      timersRef.current.push(
        setTimeout(() => {
          if (runRef.current !== runId) return;
          const photo = takePhoto();
          if (photo) images.push(photo);
          setPhotosTaken(i + 1);
          if (i === PHOTO_TIMES_MS.length - 1) {
            if (images.length) {
              sendPhotos(runId, images);
            } else {
              setCameraStatus('error');
            }
          }
        }, delay)
      );
    });
  }, [clearTimers, sendPhotos]);

  const goToCameraOrFinish = () => {
    if (isVideo) {
      setStep('camera');
      startCamera();
    } else {
      setStep('done');
    }
  };

  const handleClose = () => {
    cleanupAll();
    onClose();
  };

  const handleProceed = () => {
    cleanupAll();
    onProceed();
  };

  if (!isOpen) return null;

  const cancelButton = (
    <button
      type="button"
      onClick={handleClose}
      className="flex-1 py-2.5 sm:py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors text-sm"
    >
      {t('Cancel')}
    </button>
  );

  const primaryClass =
    'flex-[2] py-2.5 sm:py-3 bg-gradient-to-r from-electric-blue to-indigo-600 hover:from-electric-blue/90 hover:to-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-electric-blue/20 transition-all text-sm sm:text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed';

  const startButton = (
    <button type="button" data-testid="readiness-start-btn" onClick={handleProceed} className={primaryClass}>
      {t('Start Interview')}
      <ArrowRightIcon className="w-4 h-4" />
    </button>
  );

  const renderVoiceStep = () => (
    <div className="space-y-4 animate-fade-in">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-electric-blue-50 flex items-center justify-center">
          <MicrophoneIcon className="w-8 h-8 text-electric-blue" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">{t('Voice check')}</h3>
        <p className="text-sm text-gray-600 max-w-sm mx-auto">
          {t('Read the sentence below out loud, a little loud and clear — the way you will answer in the interview.')}
        </p>
      </div>

      <div className="rounded-xl border-2 border-electric-blue/30 bg-electric-blue-50/50 px-4 py-4 text-center">
        <p className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
          “{t('I am ready for my interview, and I will answer every question clearly and loudly.')}”
        </p>
      </div>

      {voiceStatus === 'listening' && (
        <div>
          <div className="relative h-4 rounded-full bg-gray-200 overflow-hidden">
            <div
              className={`h-full rounded-full transition-[width] duration-75 ${
                level >= METER_TARGET ? 'bg-emerald-500' : 'bg-amber-400'
              }`}
              style={{ width: `${level * 100}%` }}
            />
            <div className="absolute top-0 bottom-0 w-0.5 bg-gray-700" style={{ left: `${METER_TARGET * 100}%` }} />
          </div>
          <div className="flex justify-between text-[11px] text-gray-500 mt-1">
            <span>{t('Too soft')}</span>
            <span>{t('Good')}</span>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-electric-blue transition-[width] duration-150"
              style={{ width: `${speechProgress * 100}%` }}
            />
          </div>
          <p className={`mt-3 text-sm text-center font-semibold ${speakLouder ? 'text-amber-700' : 'text-gray-600'}`}>
            {speakLouder ? t('We can hear you, but please speak louder.') : t('Listening… start reading now.')}
          </p>
        </div>
      )}

      {voiceStatus === 'passed' && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3 items-center animate-bounce-in">
          <CheckCircleSolid className="w-8 h-8 text-emerald-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-emerald-900">{t('Voice checked!')}</p>
            <p className="text-xs text-emerald-700">{t('Speak just as loudly as this during your interview.')}</p>
          </div>
        </div>
      )}

      {voiceStatus === 'retry' && voiceResult?.reason && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-2.5">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">{t(VOICE_RETRY_MESSAGES[voiceResult.reason])}</p>
        </div>
      )}

      {/* Dev-only numbers for tuning TARGET_LEVEL_DB / MIN_SNR_DB on real devices.
          dBFS is negative: closer to 0 is louder. */}
      {!isProdHost() && voiceResult && voiceStatus !== 'listening' && (
        <p className="text-[11px] text-center font-mono text-gray-500" data-testid="readiness-voice-db">
          voice {voiceResult.voiceDb} dB (need ≥ {TARGET_LEVEL_DB}) · room {voiceResult.noiseDb} dB · gap{' '}
          {voiceResult.voiceDb - voiceResult.noiseDb} dB (need ≥ {MIN_SNR_DB})
        </p>
      )}

      {voiceStatus === 'error' && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-2.5">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">
            {t("We couldn't use your microphone. Allow microphone access in your browser, close other apps using it, and try again.")}
          </p>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        {cancelButton}
        {voiceStatus === 'passed' ? (
          <button type="button" onClick={goToCameraOrFinish} className={primaryClass}>
            {isVideo ? t('Next: camera check') : t('Continue')}
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={startVoiceCheck}
            disabled={voiceStatus === 'listening'}
            className={primaryClass}
          >
            {voiceStatus === 'error' || voiceStatus === 'retry' ? (
              <ArrowPathIcon className="w-4 h-4" />
            ) : (
              <MicrophoneIcon className="w-4 h-4" />
            )}
            {voiceStatus === 'listening'
              ? t('Listening…')
              : voiceStatus === 'error' || voiceStatus === 'retry'
                ? t('Try again')
                : t('Start voice check')}
          </button>
        )}
      </div>
    </div>
  );

  const renderCameraStep = () => {
    const busy = cameraStatus === 'capturing' || cameraStatus === 'checking';
    const tips = result.issues.length ? result.issues : ['FACE_NOT_VISIBLE'];
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="text-center">
          <h3 className="text-lg font-bold text-gray-900 mb-1">{t('Camera check')}</h3>
          <p className="text-sm text-gray-600 max-w-sm mx-auto">
            {t('Sit down where you will take the interview, in good light, with your face inside the oval. Then tap "Check me" and stay still for 10 seconds.')}
          </p>
        </div>

        <div
          className="rounded-xl overflow-hidden bg-gray-900 relative mx-auto w-full"
          style={portrait ? { aspectRatio: '3 / 4', maxWidth: '20rem' } : { aspectRatio: '16 / 9' }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            onLoadedMetadata={(e) => setPortrait(e.currentTarget.videoHeight > e.currentTarget.videoWidth)}
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          {/* Framing guide: head inside the oval, roughly in the middle */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              className={`rounded-[50%] border-[3px] border-dashed ${
                cameraStatus === 'passed' ? 'border-emerald-400' : 'border-white/80'
              }`}
              style={portrait ? { width: '62%', height: '58%', marginTop: '-8%' } : { width: '32%', height: '72%', marginTop: '-6%' }}
            />
          </div>
          {cameraStatus === 'starting' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
            </div>
          )}
          {busy && (
            <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-xs font-semibold py-2 text-center">
              {cameraStatus === 'capturing'
                ? t('Stay still… photo {count} of 3', { count: Math.min(photosTaken + 1, 3) })
                : t('Checking how you appear…')}
            </div>
          )}
          {cameraStatus === 'passed' && (
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-emerald-500/90 text-white text-[11px] font-semibold rounded-full px-2 py-0.5">
              <CheckCircleSolid className="w-3.5 h-3.5" />
              {t('Ready')}
            </div>
          )}
        </div>

        {cameraStatus === 'passed' && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3 items-center animate-bounce-in">
            <CheckCircleSolid className="w-8 h-8 text-emerald-500 flex-shrink-0" />
            <p className="text-sm font-semibold text-emerald-900">
              {result.message || t('You look ready. Keep sitting just like this during the interview.')}
            </p>
          </div>
        )}

        {cameraStatus === 'failed' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            {result.message ? (
              <p className="flex gap-2 text-sm font-semibold text-amber-900">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                {result.message}
              </p>
            ) : (
              <>
                <p className="text-sm font-bold text-amber-900 mb-1.5">{t('Please fix this and check again:')}</p>
                <ul className="space-y-1">
                  {tips.map((code) => (
                    <li key={code} className="flex gap-2 text-sm text-amber-800">
                      <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      {t(ISSUE_TIPS[code] || ISSUE_TIPS.FACE_NOT_VISIBLE)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {cameraStatus === 'error' && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-2.5">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">
              {t("We couldn't use your camera. Allow camera access in your browser, close other apps using it, and try again.")}
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          {cancelButton}
          {cameraStatus === 'passed' ? (
            startButton
          ) : cameraStatus === 'error' ? (
            <button type="button" onClick={startCamera} className={primaryClass}>
              <ArrowPathIcon className="w-4 h-4" />
              {t('Try again')}
            </button>
          ) : (
            <button
              type="button"
              onClick={startCameraCheck}
              disabled={busy || cameraStatus === 'starting'}
              className={primaryClass}
            >
              {busy ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <CameraIcon className="w-4 h-4" />
              )}
              {busy ? t('Checking…') : cameraStatus === 'failed' ? t('Check me again') : t('Check me')}
            </button>
          )}
        </div>

        {cameraStatus === 'failed' && failedAttempts >= ATTEMPTS_BEFORE_SKIP && (
          <button
            type="button"
            onClick={handleProceed}
            className="w-full text-xs text-gray-500 underline hover:text-gray-700"
          >
            {t('Continue to the interview anyway')}
          </button>
        )}
      </div>
    );
  };

  const renderDoneStep = () => (
    <div className="space-y-4 animate-fade-in">
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-4 flex items-center gap-3">
        <CheckCircleSolid className="w-8 h-8 text-emerald-500 flex-shrink-0" />
        <div>
          <p className="text-sm font-bold text-emerald-900">{t('All checks passed!')}</p>
          <p className="text-xs text-emerald-700">{t('Speak just as loudly as this during your interview.')}</p>
        </div>
      </div>
      <div className="flex gap-3">
        {cancelButton}
        {startButton}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-slide-up flex flex-col"
        style={{ maxHeight: 'calc(100dvh - 24px)' }}
      >
        <div className="bg-gradient-to-r from-electric-blue to-indigo-600 px-5 py-4 sm:px-6 sm:py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/20 flex items-center justify-center">
              {step === 'camera' ? (
                <VideoCameraIcon className="w-5 h-5 text-white" />
              ) : (
                <MicrophoneIcon className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">{t('Interview check')}</h2>
              <p className="text-[11px] sm:text-xs text-white/80">
                {isVideo
                  ? t('Step {current} of 2', { current: step === 'voice' ? 1 : 2 })
                  : t('Get ready to speak clearly')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            aria-label={t('Close')}
          >
            <XMarkIcon className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5 sm:p-6 overflow-y-auto">
          {step === 'voice' && renderVoiceStep()}
          {step === 'camera' && renderCameraStep()}
          {step === 'done' && renderDoneStep()}
        </div>
      </div>
    </div>
  );
};

export default InterviewReadinessModal;
